// src/pages/supplier/ExportCertificationPage.jsx
// Phase 6 — M6: Supplier export certification & customs declaration management

import { useEffect, useState } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const CERT_LABELS = {
  iec:      { label: 'Importer Exporter Code',       short: 'IEC',      color: '#3B82F6', required: true },
  rcmc:     { label: 'RCMC Certificate',              short: 'RCMC',     color: '#8B5CF6', required: true },
  ad_code:  { label: 'Authorised Dealer Code',        short: 'AD Code',  color: '#6366F1', required: false },
  fssai:    { label: 'FSSAI Food Safety',             short: 'FSSAI',    color: '#10B981', required: false },
  brc:      { label: 'BRC Global Standard',           short: 'BRC',      color: '#F59E0B', required: false },
  oeko_tex: { label: 'OEKO-TEX Standard 100',         short: 'OEKO-TEX', color: '#EF4444', required: false },
  gots:     { label: 'Global Organic Textile Std',    short: 'GOTS',     color: '#059669', required: false },
  reach:    { label: 'EU REACH Compliance',           short: 'REACH',    color: '#DC2626', required: false },
}

const CUSTOMS_STATUS = {
  draft:     { bg: '#F3F4F6', color: '#6B7280', label: 'Draft'     },
  submitted: { bg: '#DBEAFE', color: '#1E40AF', label: 'Submitted' },
  cleared:   { bg: '#DCFCE7', color: '#166534', label: 'Cleared'   },
  held:      { bg: '#FEF3C7', color: '#92400E', label: 'Held'      },
  rejected:  { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected'  },
}

function CertBadge({ certType }) {
  const meta = CERT_LABELS[certType] ?? { short: certType, color: GREY }
  return (
    <span style={{ background: meta.color + '22', color: meta.color, padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 800 }}>
      {meta.short}
    </span>
  )
}

function StatusPill({ status }) {
  const s = CUSTOMS_STATUS[status] ?? { bg: GR_LT, color: GREY, label: status }
  return <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{s.label}</span>
}

function ReadinessCard({ data }) {
  const { is_ready, has_iec, has_rcmc, has_ad_code } = data ?? {}
  return (
    <div style={{
      background: is_ready ? '#F0FDF4' : '#FFFBEB',
      border: `2px solid ${is_ready ? '#10B981' : '#F59E0B'}`,
      borderRadius: 16, padding: 20, marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: '1.4rem' }}>{is_ready ? '✅' : '⚠️'}</span>
        <div>
          <div style={{ fontWeight: 800, color: DARK, fontSize: '0.95rem' }}>
            {is_ready ? 'Export Ready' : 'Not Yet Export Ready'}
          </div>
          <div style={{ fontSize: '0.8rem', color: GREY }}>
            {is_ready ? 'You are cleared to accept international orders.' : 'Complete required certifications to accept international orders.'}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['IEC', has_iec, true], ['RCMC', has_rcmc, false], ['AD Code', has_ad_code, false]].map(([label, ok, req]) => (
          <div key={label} style={{
            padding: '5px 12px', borderRadius: 20,
            background: ok ? '#DCFCE7' : req ? '#FEE2E2' : '#F3F4F6',
            color: ok ? '#166534' : req ? '#991B1B' : '#6B7280',
            fontSize: '0.78rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5
          }}>
            <span>{ok ? '✓' : '○'}</span> {label}{req ? ' *' : ''}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ExportCertificationPage() {
  const [summary, setSummary]       = useState(null)
  const [customs, setCustoms]       = useState(null)
  const [loading, setLoading]       = useState(true)
  const [tab, setTab]               = useState('certs')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeclModal, setShowDeclModal] = useState(false)
  const [certForm, setCertForm]     = useState({ cert_type: 'iec', cert_number: '', issuing_authority: '', issue_date: '', expiry_date: '' })
  const [declForm, setDeclForm]     = useState({ order_id: '', incoterm: 'FOB' })
  const [saving, setSaving]         = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      api('/supplier/export/summary'),
      api('/supplier/export/customs/summary'),
    ]).then(([sRes, cRes]) => {
      setSummary(sRes.data ?? sRes)
      setCustoms(cRes.data ?? cRes)
      setLoading(false)
    })
  }

  useEffect(() => { load() }, [])

  const addCert = async () => {
    setSaving(true)
    const r = await api('/supplier/export/certifications', { method: 'POST', body: JSON.stringify(certForm) })
    setSaving(false)
    if (r.status === 'success' || r.data) { setShowAddModal(false); load() }
    else alert(r.message ?? 'Error saving certification')
  }

  const generateDecl = async () => {
    setSaving(true)
    const r = await api('/supplier/export/customs/generate', { method: 'POST', body: JSON.stringify(declForm) })
    setSaving(false)
    if (r.status === 'success' || r.data) { setShowDeclModal(false); load() }
    else alert(r.message ?? 'Could not generate declaration')
  }

  const submitDecl = async (id) => {
    await api(`/supplier/export/customs/${id}/submit`, { method: 'POST' })
    load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading export data…</div>

  const certs   = summary?.certifications ?? []
  const decls   = customs?.recent          ?? []
  const readiness = summary?.export_ready ?? {}

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Export Management</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Certifications, customs declarations, and international compliance</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowDeclModal(true)} style={{ padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${ORG}`, background: '#fff', color: ORG, fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}>
            + Customs Declaration
          </button>
          <button onClick={() => setShowAddModal(true)} style={{ padding: '9px 16px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}>
            + Add Certification
          </button>
        </div>
      </div>

      {/* Readiness card */}
      <ReadinessCard data={readiness} />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Certifications', value: certs.length, color: PINK },
          { label: 'Expiring Soon',  value: summary?.expiring_soon ?? 0, color: summary?.expiring_soon > 0 ? ORG : '#10B981' },
          { label: 'Customs Drafts', value: customs?.draft ?? 0,     color: GREY },
          { label: 'Cleared',        value: customs?.cleared ?? 0,   color: '#10B981' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontWeight: 600, color: DARK, fontSize: '0.84rem', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: GR_LT, borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {[['certs','Certifications'],['customs','Customs Declarations']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', background: tab===k ? '#fff' : 'transparent', color: tab===k ? PINK : '#6B7280', boxShadow: tab===k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
        ))}
      </div>

      {/* ── Certifications ── */}
      {tab === 'certs' && (
        <div>
          {certs.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', color: GREY, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
              No certifications yet. Add your IEC first to enable international orders.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
              {certs.map(cert => {
                const meta    = CERT_LABELS[cert.cert_type] ?? { color: GREY }
                const days    = cert.expiry_date ? Math.floor((new Date(cert.expiry_date) - new Date()) / 86400000) : null
                const expired = days !== null && days < 0
                const expiring = days !== null && days >= 0 && days <= 60
                return (
                  <div key={cert.id} style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `4px solid ${meta.color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <CertBadge certType={cert.cert_type} />
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: cert.is_verified ? '#10B981' : ORG }}>
                        {cert.is_verified ? '✓ Verified' : '○ Pending Verification'}
                      </span>
                    </div>
                    <div style={{ fontWeight: 700, color: DARK, marginBottom: 4, fontSize: '0.9rem' }}>{CERT_LABELS[cert.cert_type]?.label ?? cert.cert_type}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#374151', marginBottom: 8 }}>{cert.cert_number}</div>
                    {cert.issuing_authority && <div style={{ fontSize: '0.78rem', color: GREY, marginBottom: 6 }}>{cert.issuing_authority}</div>}
                    {cert.expiry_date && (
                      <div style={{ fontSize: '0.78rem', color: expired ? '#EF4444' : expiring ? ORG : '#6B7280', fontWeight: 600 }}>
                        {expired ? '⚠️ Expired' : expiring ? `⚠️ Expires in ${days} days` : `✓ Expires ${new Date(cert.expiry_date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Customs Declarations ── */}
      {tab === 'customs' && (
        <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          {decls.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: GREY }}>No customs declarations yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Declaration No.','Order','Destination','Value (INR)','Duty Est.','Incoterm','Status','Action'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {decls.map((d, i) => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontSize: '0.84rem', fontFamily: 'monospace' }}>{d.declaration_number}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: '#374151' }}>#{d.order_id}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK }}>{d.destination_country}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: DARK }}>₹{parseFloat(d.declared_value_inr ?? 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: ORG, fontWeight: 600 }}>₹{parseFloat(d.customs_duty_estimate ?? 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: GREY }}>{d.incoterm}</td>
                    <td style={{ padding: '12px 14px' }}><StatusPill status={d.status} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      {d.status === 'draft' && (
                        <button onClick={() => submitDecl(d.id)} style={{ padding: '4px 10px', borderRadius: 7, border: 'none', background: PINK, color: '#fff', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>Submit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Add Certification Modal ── */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Add Export Certification</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Certification Type *</label>
                <select value={certForm.cert_type} onChange={e => setCertForm(f => ({...f, cert_type: e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                  {Object.entries(CERT_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v.short} — {v.label}{v.required ? ' (Required)' : ''}</option>
                  ))}
                </select>
              </div>
              {[['cert_number','Certificate Number *','text'],['issuing_authority','Issuing Authority','text'],['issue_date','Issue Date','date'],['expiry_date','Expiry Date','date']].map(([k,l,t]) => (
                <div key={k}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>{l}</label>
                  <input type={t} value={certForm[k]} onChange={e => setCertForm(f => ({...f, [k]: e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={addCert} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Add Certification'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Generate Declaration Modal ── */}
      {showDeclModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Generate Customs Declaration</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Order ID *</label>
                <input type="number" value={declForm.order_id} onChange={e => setDeclForm(f => ({...f, order_id: e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Incoterm</label>
                <select value={declForm.incoterm} onChange={e => setDeclForm(f => ({...f, incoterm: e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                  {['FOB','CIF','EXW','DDP'].map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowDeclModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={generateDecl} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: ORG, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Generating…' : 'Generate Declaration'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
